import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
type VoteSummaryVariant = "default" | "memes";
interface WaveDropVoteSummaryProps {
  readonly drop: ExtendedDrop;
  readonly isWinner: boolean;
  readonly isVotingEnded: boolean;
  readonly canShowVote: boolean;
  readonly onVoteClick: () => void;
  readonly variant?: VoteSummaryVariant | undefined;
}
const VOTE_SUMMARY_STYLES: Record<VoteSummaryVariant, {
  container: string;
  header: string;
  ratingText: string;
  predictedText: string;
  totalText: string;
  button: string;
}> = {
  default: {
    container: "tw-inline-flex tw-items-center tw-gap-3 tw-p-1.5 tw-bg-iron-950 tw-border tw-border-solid tw-border-white/10 tw-rounded-lg tw-shadow-2xl",
    header: "tw-px-4 tw-flex tw-items-baseline tw-gap-1.5 tw-cursor-default",
    ratingText: "tw-text-base tw-font-bold tw-text-white tw-tabular-nums",
    predictedText: "tw-text-base tw-font-bold tw-tabular-nums tw-cursor-help",
    totalText: "tw-text-sm tw-text-iron-500 tw-font-normal",
    button: "tw-px-6 tw-py-2.5 tw-text-sm tw-bg-primary-500 tw-ring-primary-500 hover:tw-bg-primary-600 hover:tw-ring-primary-600 tw-text-white tw-flex tw-items-center tw-cursor-pointer tw-rounded-lg tw-font-semibold tw-border-0 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out"
  },
  memes: {
    container: "tw-flex tw-items-center tw-gap-2 sm:tw-gap-3 tw-p-1.5 tw-bg-iron-950 tw-border tw-border-solid tw-border-white/10 tw-rounded-lg tw-shadow-2xl tw-transition-transform hover:tw-scale-[1.01]",
    header: "tw-px-2 sm:tw-px-4 tw-flex tw-flex-wrap tw-items-baseline tw-gap-1.5 tw-cursor-default",
    ratingText: "tw-text-sm sm:tw-text-base tw-font-semibold tw-text-white tw-tabular-nums",
    predictedText: "tw-text-sm sm:tw-text-base tw-font-semibold tw-tabular-nums tw-cursor-help",
    totalText: "tw-text-sm tw-text-iron-500 tw-font-normal tw-whitespace-nowrap",
    button: "tw-px-4 tw-py-2.5 tw-text-sm tw-bg-primary-500 tw-ring-primary-500 hover:tw-bg-primary-600 hover:tw-ring-primary-600 tw-text-white tw-flex tw-items-center tw-cursor-pointer tw-rounded-lg tw-font-semibold tw-border-0 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out"
  }
};
export const WaveDropVoteSummary = t0 => {
  const $ = _c(32);
  const {
    drop,
    isWinner,
    isVotingEnded,
    canShowVote,
    onVoteClick,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const hasUserVoted = drop.context_profile_context?.rating !== undefined && drop.context_profile_context?.rating !== 0;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const isUserVoteNegative = userVote < 0;
  const shouldShowUserVote = (isVotingEnded || isWinner) && hasUserVoted;
  const styles = VOTE_SUMMARY_STYLES[variant];
  const t2 = styles.container;
  const t3 = styles.header;
  const t4 = styles.ratingText;
  let t5;
  if ($[0] !== drop.rating) {
    t5 = formatNumberWithCommas(drop.rating);
    $[0] = drop.rating;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== styles.ratingText || $[3] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[2] = styles.ratingText;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== drop.id || $[6] !== drop.rating || $[7] !== drop.rating_prediction || $[8] !== styles.predictedText) {
    t7 = drop.rating !== drop.rating_prediction && <><FontAwesomeIcon icon={faArrowRight} className="tw-flex-shrink-0 tw-size-2.5 tw-text-iron-600" /><span className={`${styles.predictedText} ${drop.rating < drop.rating_prediction ? "tw-text-emerald-400" : "tw-text-rose-400"}`} data-tooltip-id={`drop-vote-progress-${drop.id}`}>{formatNumberWithCommas(drop.rating_prediction)}</span><Tooltip id={`drop-vote-progress-${drop.id}`} place="top" offset={8} opacity={1} style={TOOLTIP_STYLES}>Projected vote count at decision time</Tooltip></>;
    $[5] = drop.id;
    $[6] = drop.rating;
    $[7] = drop.rating_prediction;
    $[8] = styles.predictedText;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  let t9;
  if ($[10] !== styles.totalText || $[11] !== t8) {
    t9 = <span className={styles.totalText}>{t8} Total</span>;
    $[10] = styles.totalText;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== styles.header || $[14] !== t6 || $[15] !== t7 || $[16] !== t9) {
    t10 = <div className={t3}>{t6}{t7}{t9}</div>;
    $[13] = styles.header;
    $[14] = t6;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== drop.wave.voting_credit_type || $[19] !== isUserVoteNegative || $[20] !== shouldShowUserVote || $[21] !== userVote) {
    t11 = shouldShowUserVote && <div className="tw-px-4 tw-flex tw-items-baseline tw-gap-1 tw-border-l tw-border-solid tw-border-white/5 tw-border-y-0 tw-border-r-0"><span className="tw-text-sm tw-font-normal tw-text-iron-500">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:</span><span className={`tw-text-sm tw-font-semibold ${isUserVoteNegative ? "tw-text-rose-500" : "tw-text-emerald-500"}`}>{isUserVoteNegative && "-"}{formatNumberWithCommas(Math.abs(userVote))}</span><span className="tw-text-iron-500 tw-text-sm tw-font-normal">{WAVE_VOTING_LABELS[drop.wave.voting_credit_type]}</span></div>;
    $[18] = drop.wave.voting_credit_type;
    $[19] = isUserVoteNegative;
    $[20] = shouldShowUserVote;
    $[21] = userVote;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== canShowVote || $[24] !== onVoteClick || $[25] !== styles.button) {
    t12 = canShowVote && <button type="button" onClick={onVoteClick} className={styles.button}>Vote</button>;
    $[23] = canShowVote;
    $[24] = onVoteClick;
    $[25] = styles.button;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== styles.container || $[28] !== t10 || $[29] !== t11 || $[30] !== t12) {
    t13 = <div className={t2}>{t10}{t11}{t12}</div>;
    $[27] = styles.container;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};